import { useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { issuancesApi } from '../issuanceApi';
import type { IssuanceDetail } from '../issuanceTypes';
import { storageFacilitiesApi } from '../inventoryApi';
import type { StorageFacilityOption } from '../inventoryTypes';
import { useEffect } from 'react';

/**
 * Record stock coming back from an assignment or a lend.
 *
 * **The issuance is not asked for** — the reader chose it by opening a row's menu — so it is
 * stated as a read-only fact in the Table Display View rather than offered as a picker locked to
 * one value. That is the shape `<StockAdjustmentModal>` already takes.
 *
 * **A return is an EVENT, not a state.** Five of ten chairs can come back this week and the rest
 * next month, so each return is its own row with its own date; the line's outstanding count is
 * what they add up to.
 */
export function RecordReturnModal({
  open,
  issuance,
  onClose,
  onSaved,
  initialLineId = null,
}: {
  open: boolean;
  issuance: IssuanceDetail;

  /**
   * The LINE the reader pressed Return on, where the surface that opened this names one — the
   * personnel profile's Issued Assets panel lists articles rather than slips. Honoured only
   * while that line still has something out; otherwise the first outstanding line is chosen,
   * which is what every other caller gets.
   */
  initialLineId?: string | null;
  onClose: () => void;
  /**
   * Saved — and WHICH GROUPS moved with it, as item TYPE ids.
   *
   * Stock Levels groups its rows by item type and fetches one group's figures at a time, so a
   * caller that reloads the whole table on a save throws away the tab, the scroll and every
   * other group's open state to report a change to one of them. A caller with no such table
   * ignores the argument, which is why it costs nothing.
   *
   * **Every type on the slip, not the one it was seeded with.** A reader may add lines beside
   * the item they opened this for, and a scoped reload that named only the seed would leave
   * the others showing figures that have just moved — which is worse than reloading all of it.
   */
  onSaved: (movedTypeIds: string[]) => void;
}) {
  const canReadFacilities = usePermission('storage-facilities.read');

  // Only lines with something still out can be returned against — a settled line in the picker
  // is a row that can only produce a rejection.
  const outstanding = useMemo(
    () => issuance.items.filter((line) => line.quantity - line.quantityReturned > 0),
    [issuance.items],
  );

  const [lineId, setLineId] = useState(
    (outstanding.some((l) => l.id === initialLineId) ? initialLineId : null)
      ?? outstanding[0]?.id
      ?? '',
  );
  const [quantity, setQuantity] = useState('1');
  const [returnedOn, setReturnedOn] = useState(todayCalendarDate());
  const [facilityId, setFacilityId] = useState('');
  const [condition, setCondition] = useState('');
  const [notes, setNotes] = useState('');

  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open || !canReadFacilities) return;

    const controller = new AbortController();

    storageFacilitiesApi
      .options(controller.signal)
      .then(setFacilities)
      .catch(() => undefined);

    return () => controller.abort();
  }, [open, canReadFacilities]);

  const line = outstanding.find((l) => l.id === lineId) ?? null;
  const remaining = line ? line.quantity - line.quantityReturned : 0;
  const serialized = line?.stockUnitId !== null && line?.stockUnitId !== undefined;

  const quantityValue = Number(quantity);
  const quantityValid =
    Number.isFinite(quantityValue) && quantityValue > 0 && quantityValue <= remaining;

  const valid = line !== null && quantityValid && returnedOn !== '';
  const canSave = valid && confirmed && !saving;

  const missing =
    line === null
      ? 'Choose the line the stock is coming back against.'
      : !quantityValid
        ? `Enter a quantity between 1 and ${remaining}.`
        : returnedOn === ''
          ? 'Set the date it came back.'
          : !confirmed
            ? 'Tick the confirmation to record the return.'
            : null;

  async function save() {
    if (!canSave || line === null) return;

    setSaving(true);
    setError(null);

    try {
      await issuancesApi.recordReturn(issuance.id, {
        issuanceItemId: line.id,

        // A named unit comes back one at a time, so the field is locked to 1 rather than
        // trusted — the server refuses anything else.
        quantity: serialized ? 1 : quantityValue,
        returnedOn,
        storageFacilityId: facilityId === '' ? null : facilityId,
        condition: condition.trim() || null,
        notes: notes.trim() || null,
      });

      // ONE line, so one group — a return is recorded against a single line of the slip.
      onSaved([line.inventoryItemTypeId]);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not record the return.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'issuance-return' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Record return — ${issuance.issuanceNumber}`}
      size="lg"
      id="issuance-return-modal"
      confirm={{
        id: 'issuance-return-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="issuance-return-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Tooltip disabledReason={canSave ? null : missing} block={false}>
            <Button
              id="issuance-return-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The issuance is a FACT being shown rather than a value being asked for, which is the
          test separating the Table Display View from a form field.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Issuance', value: issuance.issuanceNumber, mono: true },
            { label: 'Method', value: issuance.issuanceTypeLabel },
            {
              label: 'Still out',
              value: String(issuance.outstandingQuantity),
              mono: true,
            },
          ]}
        />

        <Select
          id="issuance-return-issuance-item-id"
          label="Line"
          required
          description="Which of this issuance's lines the stock is coming back against."
          value={lineId}
          onChange={(next) => {
            setLineId(next);
            setQuantity('1');
          }}
          options={outstanding.map((l) => ({
            /*
              THE PRODUCT IS THE LABEL, the profile's Item column's own value — a serial alone
              left the reader matching a code against the slip to know which laptop came back.
              The serial moves to the sublabel and the SKU stays searchable.
            */
            value: l.id,
            label: l.itemIdentity,
            sublabel: [
              l.serialNumber,
              `${l.quantity - l.quantityReturned} of ${l.quantity} still out`,
              l.storageFacilityName,
            ]
              .filter(Boolean)
              .join(' · '),
            keywords: [l.sku, l.serialNumber, l.itemName].filter(Boolean).join(' '),
          }))}
          sort="none"
          clearable={false}
          disabled={saving}
          error={error?.fieldError('issuanceItemId')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="issuance-return-quantity"
            label="Quantity"
            required
            type="number"
            min={1}
            max={remaining}
            description={
              serialized
                ? 'A named unit comes back one at a time.'
                : `At most ${remaining}, which is what is still out on this line.`
            }
            value={serialized ? '1' : quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={saving || serialized}
            error={
              quantityValid || quantity === ''
                ? error?.fieldError('quantity')
                : `Enter a quantity between 1 and ${remaining}.`
            }
          />

          <DateField
            id="issuance-return-returned-on"
            label="Came back on"
            required
            description="Backdating is ordinary — record the day it physically came back, not the day you keyed it."
            max={todayCalendarDate()}
            value={returnedOn}
            onChange={setReturnedOn}
            disabled={saving}
            error={error?.fieldError('returnedOn')}
          />

          {canReadFacilities ? (
            <Select
              id="issuance-return-storage-facility-id"
              label="Back into"
              description="Where the stock is going. Leave empty to return it to the store it left from."
              placeholder={line ? line.storageFacilityName : 'Where it left from'}
              value={facilityId}
              onChange={setFacilityId}
              options={facilities.map((f) => ({ value: f.id, label: f.path, keywords: f.code }))}
              remote
              disabled={saving}
              error={error?.fieldError('storageFacilityId')}
            />
          ) : null}

          <TextField
            id="issuance-return-condition"
            label="Condition"
            description="What state it came back in. Free text — nothing is totalled by it."
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            maxLength={200}
            disabled={saving}
            error={error?.fieldError('condition')}
          />
        </div>

        <TextField
          id="issuance-return-notes"
          label="Notes"
          description="Optional."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('notes')}
        />

        {/*
          Stated rather than inferred. A unit that came back broken is marked damaged on the
          stock-unit form, which is where that decision lives and where it writes its own ledger
          line — reading a status out of typed prose would be this form deciding something
          another module owns.
        */}
        <p className="text-xs text-slate-500">
          Returned stock comes back available whatever condition is recorded. If it came back
          broken, mark the unit damaged from Stock Units afterwards.
        </p>
      </div>
    </Modal>
  );
}
